import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Form } from "reactstrap";
import { MAIN_LANDING_PAGE, RoutePaths } from "../constants";
import { AppContext } from "../AppContext";
import { throwExpression } from "../util";
import { extractParamsFromUrl } from "../shared-links";
import { isModelValid } from "../models/data.model";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRefresh } from "@fortawesome/free-solid-svg-icons";
import { ResetForecastModal } from "../components/ResetForecastModal";
import { CopyrightFooter } from "../CopyrightFooter";
import { BrandLinkToLandingPage } from "./header/Brand";
import { RoundedCard } from "./RoundedCard";

export const LandingPage = () => {
  const navigate = useNavigate();
  const { model, setModel, setActiveTab, reset } =
    useContext(AppContext) ?? throwExpression("No AppContext");

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const modelFromUrl = extractParamsFromUrl(searchParams);
    setSearchParams(searchParams);
    if (modelFromUrl === undefined) return;
    if (!isModelValid(modelFromUrl)) {
      console.log("Error: URL params are invalid.");
      setModel({});
      setActiveTab(0);
      return;
    }
    setModel(modelFromUrl);
    setActiveTab(4);
  }, [model, setModel, setActiveTab, searchParams, setSearchParams]);

  const valid = isModelValid(model);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setActiveTab(valid ? 4 : 0);
    navigate(RoutePaths.calculator);
  };

  const [newForecastModalOpen, setNewForecastModalOpen] = useState(false);
  const toggleNewForecastModal = () =>
    setNewForecastModalOpen(!newForecastModalOpen);

  let foundForecast;
  if (valid) {
    const name = model.forecastName ? (
      <>
        {" "}
        named <br />
        <b>{model.forecastName}</b>
      </>
    ) : undefined;

    foundForecast = <span>There is an existing forecast{name}.</span>;
  }

  return (
    <div className="p-2 mb-3">
      <RoundedCard
        style={{ maxWidth: "45em", margin: "auto", marginTop: ".5em" }}
        id="landing-page"
      >
        <div
          className="py-4 px-3"
          style={{
            backgroundSize: "cover",
            backgroundImage: "url(img/landing_image_1.jpg)",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            backgroundColor: "rgba(255,255,255,0.85)",
            backgroundBlendMode: "lighten",
          }}
        >
          <div
            className="d-flex justify-content-center mb-2"
            style={{ marginTop: "-1em" }}
          >
            <BrandLinkToLandingPage />
          </div>
          <h4>HerdPlan Forecast tool</h4>
          <p>
            This tool can help beef and sheep farmers to forecast the value of
            their breeding herd or flock. It is aimed at farmers (and their
            vets) who want to better manage their livestock health risks.
          </p>
          <h4>How can the HerdPlan Forecast tool help me?</h4>
          <p>
            The tool produces three forecasts: a hypothetical pessimistic "worst
            case", a hypothetical optimistic "best case", and a custom scenario
            where you can change the parameters for your own herd or flock to
            allow you to better understand the business opportunities and risks
            associated with livestock health. The tool visually presents the
            potential financial gain from improved health, the financial risk of
            poor health outcomes, and the difference between the two. By using
            farm-specific parameters of performance and real market prices the
            tool also facilitates informed decision-making on improving health
            interventions and health planning activities.
          </p>
          <p>
            Mortality, fertility and growth rates all play a part in the value
            of a breeding herd or flock. By changing these parameters you can
            explore their effects on your business.
          </p>
          <p>
            The forecast model has been developed by the{" "}
            <a
              href="https://www.sruc.ac.uk/business-services/veterinary-laboratory-services/"
              target="_blank"
              rel="noopener noreferrer"
            >
              SRUC
            </a>{" "}
            and is offered as part of the livestock health planning activities.
          </p>
          <p>
            We expect that using the tool will help you understand the business
            value of implementing an effective health plan and help you and your
            vet identify and prioritise areas for intervention through an
            effective health planning process. The tool is free to use and we do
            not store your data.
          </p>
          <p style={{ textAlign: "center" }}>{foundForecast}</p>
          <Form
            onSubmit={onSubmit}
            className="gap-2 my-3 d-flex justify-content-center"
          >
            <Button type="submit" color="dark" block>
              {valid ? "Show forecast" : "Start a forecast!"}
            </Button>
            {valid && (
              <Button
                color="dark"
                outline
                onClick={toggleNewForecastModal}
                block
              >
                <FontAwesomeIcon icon={faRefresh} className="me-2" size="sm" />
                <span>Reset</span>
              </Button>
            )}
          </Form>
          <h4>About HerdPlan</h4>
          <p>
            HerdPlan can help you and your vet to make livestock health risk
            management quick, easy and effective. Click{" "}
            <a
              href={MAIN_LANDING_PAGE}
              target="_blank"
              rel="noopener noreferrer"
            >
              here
            </a>{" "}
            to find out more.
          </p>
          <ResetForecastModal
            isOpen={newForecastModalOpen}
            toggle={toggleNewForecastModal}
            onConfirm={() => {
              reset();
              navigate(RoutePaths.calculator);
            }}
          />
        </div>
        <footer>
          <CopyrightFooter />
        </footer>
      </RoundedCard>
    </div>
  );
};
